import React, { useEffect } from "react";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Row,
  Col,
  Label,
  Input,
  FormFeedback,
} from "reactstrap";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import DatePickerField from "../../Components/Common/DatePickerField";

// Validation schema (same as AddVehicle)
const VehicleSchema = Yup.object().shape({
  // vehicleName: Yup.string().required("Vehicle name is required"),
  vehicleNo: Yup.string().required("Vehicle number is required"),
  // vehicleModelNo: Yup.string().required("Model number is required"),
  kmRate: Yup.number()
    .positive("Must be positive")
    .required("KM rate required"),
  localRate: Yup.number()
    .positive("Must be positive")
    .required("Local rate required"),
  vehicleCapacity: Yup.string().required("Capacity is required"),
  // capacityUnit: Yup.string().required("Capacity unit is required"),
});

const EditVehicleModal = ({ show, toggle, onSubmit, vehicle }) => {
  const initialValues = {
    id: vehicle?.id,
    vehicleName: vehicle?.vehicleName || "",
    vehicleNo: vehicle?.vehicleNo || "",
    vehicleModelNo: vehicle?.vehicleModelNo || "",
    kmRate: vehicle?.kmRate || "",
    localRate: vehicle?.localRate || "",
    vehicleCapacity: vehicle?.vehicleCapacity || "",
    capacityUnit: vehicle?.capacityUnit || "",
    insuranceStartDate: vehicle?.insuranceStartDate || "",
    insuranceEndDate: vehicle?.insuranceEndDate || "",
    roadTaxStartDate: vehicle?.roadTaxStartDate || "",
    roadTaxEndDate: vehicle?.roadTaxEndDate || "",
    pucStartDate: vehicle?.pucStartDate || "",
    pucEndDate: vehicle?.pucEndDate || "",
  };

  return (
    <Modal isOpen={show} toggle={toggle} centered size="lg">
      <ModalHeader toggle={toggle}>Edit Vehicle</ModalHeader>

      <Formik
        enableReinitialize
        initialValues={initialValues}
        validationSchema={VehicleSchema}
        onSubmit={(values, { resetForm }) => {
          // Remove empty fields
          const cleanedValues = Object.fromEntries(
            Object.entries(values).filter(([key, value]) => {
              if (value === "" || value === null || value === undefined)
                return false;
              if (Array.isArray(value) && value.length === 0) return false;
              if (
                typeof value === "object" &&
                !Array.isArray(value) &&
                Object.keys(value).length === 0
              )
                return false;
              return true;
            }),
          );

          if (onSubmit) onSubmit(cleanedValues);
          resetForm();
          toggle();
        }}
      >
        {({ errors, touched }) => (
          <Form>
            <ModalBody>
              <Row className="gy-3 mb-3">
                <Col md={6}>
                  <Label>Vehicle Name</Label>
                  <Field
                    name="vehicleName"
                    as={Input}
                    invalid={touched.vehicleName && !!errors.vehicleName}
                  />
                  <ErrorMessage name="vehicleName" component={FormFeedback} />
                </Col>

                <Col md={6}>
                  <Label>
                    Vehicle Number <span className="text-danger">*</span>
                  </Label>
                  <Field
                    name="vehicleNo"
                    as={Input}
                    invalid={touched.vehicleNo && !!errors.vehicleNo}
                  />
                  <ErrorMessage name="vehicleNo" component={FormFeedback} />
                </Col>

                <Col md={6}>
                  <Label>Model Number</Label>
                  <Field
                    name="vehicleModelNo"
                    as={Input}
                    invalid={touched.vehicleModelNo && !!errors.vehicleModelNo}
                  />
                  <ErrorMessage
                    name="vehicleModelNo"
                    component={FormFeedback}
                  />
                </Col>

                <Col md={6}>
                  <Label>
                    Vehicle Capacity <span className="text-danger">*</span>
                  </Label>
                  <Field
                    name="vehicleCapacity"
                    as={Input}
                    invalid={
                      touched.vehicleCapacity && !!errors.vehicleCapacity
                    }
                  />
                  <ErrorMessage
                    name="vehicleCapacity"
                    component={FormFeedback}
                  />
                </Col>

                <Col md={6}>
                  <Label>Capacity Unit</Label>
                  <Field
                    name="capacityUnit"
                    as={Input}
                    invalid={touched.capacityUnit && !!errors.capacityUnit}
                  />
                  <ErrorMessage name="capacityUnit" component={FormFeedback} />
                </Col>

                <Col md={6}>
                  <Label>
                    KM Rate <span className="text-danger">*</span>
                  </Label>
                  <Field
                    name="kmRate"
                    type="number"
                    as={Input}
                    invalid={touched.kmRate && !!errors.kmRate}
                  />
                  <ErrorMessage name="kmRate" component={FormFeedback} />
                </Col>

                <Col md={6}>
                  <Label>
                    Local Rate <span className="text-danger">*</span>
                  </Label>
                  <Field
                    name="localRate"
                    type="number"
                    as={Input}
                    invalid={touched.localRate && !!errors.localRate}
                  />
                  <ErrorMessage name="localRate" component={FormFeedback} />
                </Col>
              </Row>
              <Row className="gy-3">
                <Col md={6}>
                  <Label>Insurance Start Date</Label>
                  <div className="input-group">
                    <DatePickerField name="insuranceStartDate" />
                    <span className="input-group-text">
                      <i className="ri-calendar-2-line"></i>
                    </span>
                  </div>
                </Col>

                <Col md={6}>
                  <Label>Insurance End Date</Label>
                  <div className="input-group">
                    <DatePickerField name="insuranceEndDate" />
                    <span className="input-group-text">
                      <i className="ri-calendar-2-line"></i>
                    </span>
                  </div>
                </Col>

                <Col md={6}>
                  <Label>Road Tax Start Date</Label>
                  <div className="input-group">
                    <DatePickerField name="roadTaxStartDate" />
                    <span className="input-group-text">
                      <i className="ri-calendar-2-line"></i>
                    </span>
                  </div>
                </Col>

                <Col md={6}>
                  <Label>Road Tax End Date</Label>
                  <div className="input-group">
                    <DatePickerField name="roadTaxEndDate" />
                    <span className="input-group-text">
                      <i className="ri-calendar-2-line"></i>
                    </span>
                  </div>
                </Col>

                <Col md={6}>
                  <Label>PUC Start Date</Label>
                  <div className="input-group">
                    <DatePickerField name="pucStartDate" />
                    <span className="input-group-text">
                      <i className="ri-calendar-2-line"></i>
                    </span>
                  </div>
                </Col>

                <Col md={6}>
                  <Label>PUC End Date</Label>
                  <div className="input-group">
                    <DatePickerField name="pucEndDate" />
                    <span className="input-group-text">
                      <i className="ri-calendar-2-line"></i>
                    </span>
                  </div>
                </Col>
              </Row>
            </ModalBody>

            <ModalFooter>
              <Button color="secondary" className="btn-sm" onClick={toggle}>
                Cancel
              </Button>
              <Button color="success" className="btn-sm" type="submit">
                Save changes
              </Button>
            </ModalFooter>
          </Form>
        )}
      </Formik>
    </Modal>
  );
};

export default EditVehicleModal;
